import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Textarea,
  useToast,
} from '@chakra-ui/react'
import { Button } from '../../../../components/Button'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect } from 'react'
import { useMutation } from 'react-query'
import { api } from '../../../../services/api'
import { Input } from '../../../../components/Form/Input'
import { InputMaskForm } from '../../../../components/Form/InputMaskForm'
import { useEndereco } from '../../../../hooks/useEndereco'

const schema = z
  .object({
    cep: z.string().optional(),
    ruaEntrega: z.string().optional(),
    numeroEntrega: z.string().optional(),
    bairroEntrega: z.string().optional(),
    cidadeEntrega: z.string().optional(),
    numeroNota: z.string().optional(),
    observacao_horario: z.string().optional(),
    observacao_geral: z.string().optional(),
    complementoEntrega: z.string().optional(),
  })
  .refine(
    (data) => {
      if (
        data.cep ||
        data.ruaEntrega ||
        data.numeroEntrega ||
        data.bairroEntrega ||
        data.cidadeEntrega ||
        data.numeroNota ||
        data.observacao_geral ||
        data.complementoEntrega
      ) {
        return (
          data.cep &&
          data.ruaEntrega &&
          data.numeroEntrega &&
          data.bairroEntrega &&
          data.cidadeEntrega &&
          data.numeroNota &&
          data.observacao_geral &&
          data.complementoEntrega
        )
      }

      return true
    },
    {
      path: ['endereco'],
      message: 'Preencha todos os campos obrigatórios',
    }
  )

async function updateEntrega({
  cep,
  ruaEntrega,
  numeroEntrega,
  bairroEntrega,
  cidadeEntrega,
  numeroNota,
  observacao_horario,
  observacao_geral,
  complementoEntrega,
}) {
  const response = await api.put('/site/v2/orcamento-crm/atualizar-dados-entrega', {
    cep_entrega: cep,
    rua_entrega: ruaEntrega,
    numero_entrega: numeroEntrega,
    bairro_entrega: bairroEntrega,
    cidade_entrega: cidadeEntrega,
    observacao_horario,
    numero_nota: numeroNota,
    complemento_entrega: complementoEntrega,
    observacao_geral,
  })

  return response.data
}

export function InformacoesEntregaModal({ dadosEntrega, dadosPedido }) {
  const {
    handleSubmit,
    register,
    setValue,
    control,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(schema),
  })
  const { endereco, handleCep } = useEndereco()

  const { isOpen, onOpen, onClose } = useDisclosure()
  const toast = useToast({
    position: 'top-right',
    duration: 3000,
    isClosable: true,
  })

  const pedidoLiberado = dadosPedido?.pedido_liberado
  const pedidoAprovado = dadosPedido?.pedido_aprovado

  const { mutateAsync, isLoading } = useMutation({
    mutationFn: updateEntrega,
    onSuccess: () => {
      toast({
        title: 'Informações de entrega atualizadas com sucesso',
        status: 'success',
      })

      onClose()
    },
    onError: () => {
      toast({
        title: 'Erro ao atualizar informações de entrega',
        status: 'error',
      })
    },
  })

  useEffect(() => {
    setValue('cep', dadosEntrega.cep_entrega)
    setValue('ruaEntrega', dadosEntrega.rua_entrega)
    setValue('numeroEntrega', dadosEntrega.numero_entrega)
    setValue('bairroEntrega', dadosEntrega.bairro_entrega)
    setValue('cidadeEntrega', dadosEntrega.cidade_entrega)
    setValue('observacao_entrega', dadosEntrega.observacao_entrega)
  }, [dadosEntrega, setValue])

  useEffect(() => {
    if (endereco) {
      setValue('ruaEntrega', endereco.rua)
      setValue('bairroEntrega', endereco.bairro)
      setValue('cidadeEntrega', endereco.cidade)
    }
  }, [endereco, setValue])

  useEffect(() => {
    if (errors.endereco) {
      toast({
        title: errors.endereco.message,
        status: 'error',
      })
    }
  }, [errors.endereco, toast])

  return (
    <>
      <Button pointerEvents="auto" onClick={onOpen}>
        Informações de entrega
      </Button>

      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent
          as="form"
          onSubmit={handleSubmit((data) => {
            console.log(data)
          })}
        >
          <ModalHeader>Informações de entrega</ModalHeader>
          <ModalCloseButton />
          <ModalBody display="flex" flexDirection="column" gap={4}>
            <fieldset
              style={{
                display: 'grid',
                gap: '12px',
              }}
              disabled={pedidoLiberado || pedidoAprovado}
            >
              <input type="hidden" {...register('numeroNota')} value={dadosPedido.numero_nota} />
              <InputMaskForm
                label="CEP"
                control={control}
                id="cep"
                name="cep"
                error={errors.cep}
                mask="99999-999"
                onChangeCustom={(e) => handleCep(e.target.value)}
              />
              <Input {...register('ruaEntrega')} label="Rua" />
              <Input {...register('numeroEntrega')} label="Número" />
              <Input {...register('bairroEntrega')} label="Bairro" />
              <Input {...register('cidadeEntrega')} label="Cidade" />
              <Input {...register('complementoEntrega')} label="Complemento" />

              <Textarea {...register('observacao_horario')} placeholder="Observação horário" borderRadius="0" borderColor="gray.300" />
              <Textarea {...register('observacao_geral')} placeholder="Observação geral" borderRadius="0" borderColor="gray.300" />
            </fieldset>
          </ModalBody>

          <ModalFooter>
            <Button colorScheme="blue" mr={3} onClick={onClose} bgColor="gray.200" color="gray.800">
              Fechar
            </Button>
            <Button
              variant="ghost"
              isLoading={isLoading}
              disabled={pedidoLiberado || pedidoAprovado}
              onClick={handleSubmit((data) => {
                mutateAsync(data)
              })}
            >
              Confirmar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  )
}
